import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Building2, Settings, Link2, Loader2 } from "lucide-react";
import { PMSettings } from "@/hooks/use-property-management";

interface Props {
  settings: PMSettings[];
  portfolioProperties: { id: string; label: string }[];
  onSave: (s: Partial<PMSettings>) => void;
  isSaving: boolean;
}

export function PMSettingsTab({ settings, portfolioProperties, onSave, isSaving }: Props) {
  const [selectedProperty, setSelectedProperty] = useState<string>("");
  const [form, setForm] = useState<Partial<PMSettings>>({
    management_mode: "self",
    pm_company_name: "",
    pm_contact_name: "",
    pm_contact_email: "",
    pm_contact_phone: "",
    appfolio_property_id: "",
    appfolio_enabled: false,
  });

  // Load existing settings when property changes
  useEffect(() => {
    if (selectedProperty) {
      const existing = settings.find(s => s.pipeline_property_id === selectedProperty);
      if (existing) {
        setForm({
          management_mode: existing.management_mode,
          pm_company_name: existing.pm_company_name || "",
          pm_contact_name: existing.pm_contact_name || "",
          pm_contact_email: existing.pm_contact_email || "",
          pm_contact_phone: existing.pm_contact_phone || "",
          appfolio_property_id: existing.appfolio_property_id || "",
          appfolio_enabled: existing.appfolio_enabled,
        });
      } else {
        setForm({ management_mode: "self", pm_company_name: "", pm_contact_name: "", pm_contact_email: "", pm_contact_phone: "", appfolio_property_id: "", appfolio_enabled: false });
      }
    }
  }, [selectedProperty, settings]);

  const handleSave = () => {
    if (!selectedProperty) return;
    onSave({ ...form, pipeline_property_id: selectedProperty });
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Property Settings</CardTitle>
          <CardDescription>Configure management mode per property. Default is self-managed.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Select Property</Label>
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger><SelectValue placeholder="Choose a property to configure" /></SelectTrigger>
              <SelectContent>
                {portfolioProperties.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {selectedProperty && (
            <>
              <div className="space-y-2">
                <Label>Management Mode</Label>
                <Select value={form.management_mode} onValueChange={(v) => setForm({ ...form, management_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Self-Managed</SelectItem>
                    <SelectItem value="pm_company">Property Management Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.management_mode === "pm_company" && (
                <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">PM Company Details</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Company Name</Label>
                      <Input value={form.pm_company_name || ""} onChange={(e) => setForm({ ...form, pm_company_name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Name</Label>
                      <Input value={form.pm_contact_name || ""} onChange={(e) => setForm({ ...form, pm_contact_name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={form.pm_contact_email || ""} onChange={(e) => setForm({ ...form, pm_contact_email: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input type="tel" value={form.pm_contact_phone || ""} onChange={(e) => setForm({ ...form, pm_contact_phone: e.target.value })} />
                    </div>
                  </div>

                  {/* AppFolio Integration */}
                  <div className="border-t pt-4 mt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4" />
                        <span className="text-sm font-medium">AppFolio Integration</span>
                        <Badge variant="outline" className="text-xs">API</Badge>
                      </div>
                      <Switch
                        checked={form.appfolio_enabled || false}
                        onCheckedChange={(v) => setForm({ ...form, appfolio_enabled: v })}
                      />
                    </div>
                    {form.appfolio_enabled && (
                      <div className="space-y-2">
                        <Label>AppFolio Property ID</Label>
                        <Input
                          value={form.appfolio_property_id || ""}
                          onChange={(e) => setForm({ ...form, appfolio_property_id: e.target.value })}
                          placeholder="Enter AppFolio property ID"
                        />
                        <p className="text-xs text-muted-foreground">
                          Connect to AppFolio to sync work orders, payments, and tenant data automatically. 
                          API credentials can be configured in workspace Settings → Integrations.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Settings
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
